<template>
  <div
    ref="dragButton"
    class="floating-button-advanced"
    :class="[size, shape, { 'is-dragging': isDragging }]"
    :style="buttonStyle"
    @mousedown="handleMouseDown"
    @touchstart="handleTouchStart"
    @click="handleClick"
  >
    <slot>
      <div class="button-icon">
        <van-icon name="service-o" size="20" />
      </div>
    </slot>

    <!-- 拖动提示 -->
    <div v-if="showDragHint" class="drag-hint">
      <span>可拖动</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FloatingButtonAdvanced',

  props: {
    // 初始位置
    initialPosition: {
      type: String,
      default: 'right',
      validator: function(value) {
        return ['left', 'right', 'top', 'bottom', 'custom'].indexOf(value) !== -1
      }
    },
    // 自定义位置
    customPosition: {
      type: Object,
      default: function() {
        return { x: 20, y: 20 }
      }
    },
    // 是否吸附边缘
    snapToEdge: {
      type: Boolean,
      default: true
    },
    // 吸附阈值
    snapThreshold: {
      type: Number,
      default: 50
    },
    // 显示拖动提示
    showDragHint: {
      type: Boolean,
      default: true
    },
    // 自动隐藏
    autoHide: {
      type: Boolean,
      default: false
    },
    // 隐藏延迟（毫秒）
    hideDelay: {
      type: Number,
      default: 2000
    },
    // 主题颜色
    theme: {
      type: String,
      default: 'primary',
      validator: function(value) {
        return ['primary', 'secondary', 'success', 'warning', 'danger'].indexOf(value) !== -1
      }
    },
    size: {
      type: String,
      default: 'small',
      validator: function(value) {
        return ['small', 'normal', 'large'].indexOf(value) !== -1
      }
    },
    shape: {
      type: String,
      default: 'circle',
      validator: function(value) {
        return ['circle', 'square', 'rounded'].indexOf(value) !== -1
      }
    },
    // 点击延迟（毫秒），用于区分点击和拖动
    clickDelay: {
      type: Number,
      default: 200
    }
  },

  data() {
    return {
      position: { x: 0, y: 0 },
      isDragging: false,
      isHidden: false,
      dragOffset: { x: 0, y: 0 },
      screenSize: { width: 0, height: 0 },
      buttonSize: { width: 0, height: 0 },
      hideTimer: null,
      // 新增：用于控制点击和拖动的分离
      clickTimer: null,
      isClickable: true,
      startPosition: { x: 0, y: 0 },
      // 新增：记录是否已经移动
      hasMoved: false
    }
  },

  computed: {
    themeColors() {
      return {
        primary: { bg: '#409EFF', hover: '#66b1ff' },
        secondary: { bg: '#909399', hover: '#a6a9ad' },
        success: { bg: '#67C23A', hover: '#85ce61' },
        warning: { bg: '#E6A23C', hover: '#ebb563' },
        danger: { bg: '#F56C6C', hover: '#f78989' }
      }
    },

    buttonStyle() {
      const themeColor = this.themeColors[this.theme] || this.themeColors.primary

      const style = {
        left: `${this.position.x}px`,
        top: `${this.position.y}px`,
        transform: this.isHidden ? 'translateX(100%)' : 'translateX(0)',
        opacity: this.isHidden ? 0 : 1,
        transition: this.isDragging ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        backgroundColor: themeColor.bg,
        '--hover-color': themeColor.hover,
        cursor: this.isDragging ? 'grabbing' : 'pointer'
      }

      switch (this.size) {
        case 'small':
          style.width = '44px'
          style.height = '44px'
          break
        case 'large':
          style.width = '76px'
          style.height = '76px'
          break
        default:
          style.width = '60px'
          style.height = '60px'
      }

      switch (this.shape) {
        case 'square':
          style.borderRadius = '12px'
          break
        case 'rounded':
          style.borderRadius = '20px'
          break
        default:
          style.borderRadius = '50%'
      }

      return style
    }
  },

  watch: {
    customPosition: {
      handler(newVal) {
        if (this.initialPosition === 'custom') {
          this.position = { ...newVal }
          this.constrainToScreen()
        }
      },
      deep: true
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.init()
    })

    window.addEventListener('resize', this.updateScreenSize)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateScreenSize)
    this.clearHideTimer()
    this.clearClickTimer()

    document.removeEventListener('mousemove', this.handleMouseMove)
    document.removeEventListener('mouseup', this.handleMouseUp)
    document.removeEventListener('touchmove', this.handleTouchMove)
    document.removeEventListener('touchend', this.handleTouchEnd)
  },

  methods: {
    init() {
      this.screenSize = {
        width: window.innerWidth,
        height: window.innerHeight
      }

      if (this.$refs.dragButton) {
        const rect = this.$refs.dragButton.getBoundingClientRect()
        this.buttonSize = {
          width: rect.width,
          height: rect.height
        }
        this.initPosition()
      }

      if (this.autoHide) {
        this.startHideTimer()
      }
    },

    initPosition() {
      const { width, height } = this.buttonSize
      const screenWidth = this.screenSize.width
      const screenHeight = this.screenSize.height

      switch (this.initialPosition) {
        case 'left':
          this.position = { x: 20, y: screenHeight / 2 - height / 2 }
          break
        case 'right':
          this.position = { x: screenWidth - width - 20, y: screenHeight - screenHeight / 5 }
          break
        case 'top':
          this.position = { x: screenWidth / 2 - width / 2, y: 20 }
          break
        case 'bottom':
          this.position = { x: screenWidth / 2 - width / 2, y: screenHeight - height - 20 }
          break
        case 'custom':
          this.position = { ...this.customPosition }
          break
        default:
          this.position = { x: 20, y: 20 }
      }

      this.constrainToScreen()
    },

    snapToNearestEdge() {
      if (!this.snapToEdge) return

      const { x, y } = this.position
      const { width, height } = this.buttonSize
      const screenWidth = this.screenSize.width
      const screenHeight = this.screenSize.height

      const distances = {
        left: x,
        right: screenWidth - x - width,
        top: y,
        bottom: screenHeight - y - height
      }

      const nearestEdge = Object.entries(distances).reduce((a, b) =>
        a[1] < b[1] ? a : b
      )[0]

      if (distances[nearestEdge] < this.snapThreshold) {
        switch (nearestEdge) {
          case 'left':
            this.position.x = 20
            break
          case 'right':
            this.position.x = screenWidth - width - 20
            break
          case 'top':
            this.position.y = 20
            break
          case 'bottom':
            this.position.y = screenHeight - height - 20
            break
        }
      }

      this.$emit('position-change', { position: this.position, edge: nearestEdge })
    },

    constrainToScreen() {
      const { width, height } = this.buttonSize
      const screenWidth = this.screenSize.width
      const screenHeight = this.screenSize.height

      this.position.x = Math.max(10, Math.min(this.position.x, screenWidth - width - 10))
      this.position.y = Math.max(10, Math.min(this.position.y, screenHeight - height - 10))
    },

    // 定时器相关方法
    startHideTimer() {
      if (!this.autoHide || this.isDragging) return

      this.clearHideTimer()
      this.hideTimer = setTimeout(() => {
        this.isHidden = true
      }, this.hideDelay)
    },

    cancelHideTimer() {
      this.clearHideTimer()
      this.isHidden = false
    },

    clearHideTimer() {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer)
        this.hideTimer = null
      }
    },

    clearClickTimer() {
      if (this.clickTimer) {
        clearTimeout(this.clickTimer)
        this.clickTimer = null
      }
    },

    updateScreenSize() {
      this.screenSize = {
        width: window.innerWidth,
        height: window.innerHeight
      }
      this.constrainToScreen()
    },

    // 鼠标事件处理
    handleMouseDown(e) {
      e.preventDefault()
      e.stopPropagation()

      // 设置点击定时器，在指定时间后禁用点击
      this.isClickable = true
      this.hasMoved = false
      this.startPosition = { x: e.clientX, y: e.clientY }

      this.clickTimer = setTimeout(() => {
        this.isClickable = false
        // 如果超过点击延迟时间，开始拖动
        this.startDrag(e.clientX, e.clientY)
      }, this.clickDelay)

      document.addEventListener('mousemove', this.handleMouseMove)
      document.addEventListener('mouseup', this.handleMouseUp)
    },

    handleMouseMove(e) {
      e.preventDefault()

      // 检查是否移动了足够距离
      if (!this.hasMoved) {
        const moveX = Math.abs(e.clientX - this.startPosition.x)
        const moveY = Math.abs(e.clientY - this.startPosition.y)

        if (moveX > 5 || moveY > 5) {
          this.hasMoved = true
          this.isClickable = false
          this.clearClickTimer()
          this.startDrag(e.clientX, e.clientY)
        }
      }

      if (this.isDragging) {
        this.updatePosition(e.clientX, e.clientY)
      }
    },

    handleMouseUp(e) {
      e.preventDefault()

      this.clearClickTimer()
      document.removeEventListener('mousemove', this.handleMouseMove)
      document.removeEventListener('mouseup', this.handleMouseUp)

      if (this.isDragging) {
        this.endDrag()
      } else if (this.isClickable) {
        // 如果是可点击状态，触发点击事件
        this.$nextTick(() => {
          this.$emit('click', e)
        })
      }
    },

    // 触摸事件处理
    handleTouchStart(e) {
      e.preventDefault()
      e.stopPropagation()

      const touch = e.touches[0]
      this.isClickable = true
      this.hasMoved = false
      this.startPosition = { x: touch.clientX, y: touch.clientY }

      this.clickTimer = setTimeout(() => {
        this.isClickable = false
        this.startDrag(touch.clientX, touch.clientY)
      }, this.clickDelay)

      document.addEventListener('touchmove', this.handleTouchMove, { passive: false })
      document.addEventListener('touchend', this.handleTouchEnd)
    },

    handleTouchMove(e) {
      e.preventDefault()
      const touch = e.touches[0]

      if (!this.hasMoved) {
        const moveX = Math.abs(touch.clientX - this.startPosition.x)
        const moveY = Math.abs(touch.clientY - this.startPosition.y)

        if (moveX > 5 || moveY > 5) {
          this.hasMoved = true
          this.isClickable = false
          this.clearClickTimer()
          this.startDrag(touch.clientX, touch.clientY)
        }
      }

      if (this.isDragging) {
        this.updatePosition(touch.clientX, touch.clientY)
      }
    },

    handleTouchEnd(e) {
      e.preventDefault()

      this.clearClickTimer()
      document.removeEventListener('touchmove', this.handleTouchMove)
      document.removeEventListener('touchend', this.handleTouchEnd)

      if (this.isDragging) {
        this.endDrag()
      } else if (this.isClickable) {
        this.$nextTick(() => {
          this.$emit('click', e)
        })
      }
    },

    // 点击事件处理 - 确保只在没有拖动的情况下触发
    handleClick(e) {
      // 阻止默认行为，避免与拖动冲突
      e.preventDefault()
      e.stopPropagation()

      // 如果正在进行拖动，不触发点击
      if (this.isDragging || !this.isClickable || this.hasMoved) {
        return
      }

      // 触发点击事件
      this.$emit('click', e)
    },

    // 开始拖动
    startDrag(clientX, clientY) {
      this.cancelHideTimer()
      this.isDragging = true
      this.isClickable = false

      this.dragOffset = {
        x: clientX - this.position.x,
        y: clientY - this.position.y
      }

      this.$emit('drag-start')
    },

    // 更新位置
    updatePosition(clientX, clientY) {
      if (!this.isDragging) return

      this.position.x = clientX - this.dragOffset.x
      this.position.y = clientY - this.dragOffset.y

      this.constrainToScreen()
    },

    // 结束拖动
    endDrag() {
      if (!this.isDragging) return

      this.isDragging = false
      this.snapToNearestEdge()
      this.$emit('drag-end')

      // 重置状态
      this.isClickable = true
      this.hasMoved = false

      if (this.autoHide) {
        this.startHideTimer()
      }
    },

    // 暴露给父组件的方法
    show() {
      this.cancelHideTimer()
      this.isHidden = false
    },

    hide() {
      this.isHidden = true
    },

    updatePositionTo(x, y) {
      this.position = { x, y }
      this.constrainToScreen()
    },

    resetPosition() {
      this.initPosition()
    }
  }
}
</script>

<style scoped>
.floating-button-advanced {
  position: fixed;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  touch-action: none;
  overflow: visible;
}

.floating-button-advanced:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.2);
  background-color: var(--hover-color) !important;
}

.floating-button-advanced.is-dragging {
  cursor: grabbing !important;
  transform: scale(0.98);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
}

/* 图标 */
.button-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  pointer-events: none;
  color:#fff
}

.button-icon svg {
  width: 100%;
  height: 100%;
}

/* 拖动提示 */
.drag-hint {
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.3s;
  pointer-events: none;
}

.floating-button-advanced:hover .drag-hint {
  opacity: 1;
}

.drag-hint::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid rgba(0, 0, 0, 0.8);
}

/* 响应式 */
@media (max-width: 768px) {
  .floating-button-advanced {
    min-width: 50px;
    min-height: 50px;
  }

  .drag-hint {
    display: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/mobile/components/imDesk.vue